import { useEffect, useState } from "react";
import { api, formatCents, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Input, Select } from "@/components/ui/Primitives";
import { Sparkles, ArrowRight, TrendingUp, TrendingDown, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","NT","NU","YT"];

export default function RentAnalysis() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    address: "", city: "", province: "ON",
    property_type: "apartment", beds: 1, baths: 1, sqft: "",
    current_rent: "", year_built: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rent-analysis");
      setItems(Array.isArray(data) ? data : (data?.analyses || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const run = async (e) => {
    e.preventDefault();
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/rent-analysis", {
        address: form.address.trim(),
        city: form.city.trim(),
        province: form.province,
        property_type: form.property_type,
        beds: Number(form.beds) || 1,
        baths: Number(form.baths) || 1,
        sqft: form.sqft ? Number(form.sqft) : null,
        year_built: form.year_built ? Number(form.year_built) : null,
        current_rent: Number(form.current_rent) || 0,
      });
      setResult(data);
      toast.success("Analysis complete");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally { setRunning(false); }
  };

  const remove = async (a) => {
    const ok = await confirmDialog({
      title: "Delete this analysis?",
      description: `${a.address || "This analysis"} · ${a.city || ""} will be removed.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/rent-analysis/${a.analysis_id}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div>
      <PageHeader
        title="Rent analysis"
        subtitle="Find the optimal rent for any unit based on market comparables — powered by smart features."
      />

      <div className="grid lg:grid-cols-5 gap-5">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-green-700"/>
            <h2 className="font-display text-lg font-bold text-slate-900">Run analysis</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Fill in property details — we'll compare against the local market.</p>
          <form onSubmit={run} className="space-y-3">
            <Input label="Street address" required value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} testid="ra-address-input"/>
            <div className="grid grid-cols-2 gap-3">
              <Input label="City" required value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} testid="ra-city-input"/>
              <Select label="Province" value={form.province} onChange={(e)=>setForm({...form, province:e.target.value})}>
                {PROVINCES.map((p) => <option key={p}>{p}</option>)}
              </Select>
            </div>
            <Select label="Type" value={form.property_type} onChange={(e)=>setForm({...form, property_type:e.target.value})}>
              <option value="apartment">Apartment</option>
              <option value="condo">Condo</option>
              <option value="house">House</option>
              <option value="townhouse">Townhouse</option>
              <option value="basement">Basement</option>
              <option value="room">Room</option>
            </Select>
            <div className="grid grid-cols-3 gap-3">
              <Input label="Beds" type="number" min="0" value={form.beds} onChange={(e)=>setForm({...form, beds:e.target.value})}/>
              <Input label="Baths" type="number" min="0" step="0.5" value={form.baths} onChange={(e)=>setForm({...form, baths:e.target.value})}/>
              <Input label="Sqft" type="number" value={form.sqft} onChange={(e)=>setForm({...form, sqft:e.target.value})}/>
            </div>
            <Input label="Current rent (CAD/mo)" type="number" step="0.01" value={form.current_rent} onChange={(e)=>setForm({...form, current_rent:e.target.value})} testid="ra-rent-input"/>
            <Button type="submit" disabled={running} className="w-full" data-testid="ra-run-button">
              <Sparkles className="w-4 h-4"/> {running ? "Analyzing…" : "Run analysis"}
            </Button>
          </form>
        </Card>

        <Card className="lg:col-span-3 p-6 min-h-[400px]">
          {result ? <Result data={result}/> : (
            <div className="flex flex-col items-center justify-center text-center h-full py-10">
              <div className="w-14 h-14 rounded-full bg-green-50 text-green-800 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7"/>
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900">Run your first analysis</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-sm">Fill in the unit details on the left and click "Run analysis" to get a market-rate recommendation.</p>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-7">
        <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Recent analyses</h2>
        {loading ? <Loader/> : items.length === 0 ? (
          <EmptyState icon={Sparkles} title="No saved analyses" description="Past analyses will appear here for easy reference."/>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <Card key={a.analysis_id} className="p-4 card-lift" data-testid={`analysis-card-${a.analysis_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{a.address}</div>
                    <div className="text-xs text-slate-500 truncate">{a.city}, {a.province}</div>
                  </div>
                  <button onClick={() => remove(a)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div className="font-display text-xl font-bold text-green-800">{formatCents((a.recommended_rent || a.suggested_rent || 0) * 100)}</div>
                  <div className="text-xs text-slate-500">{formatDate(a.created_at)}</div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Result({ data }) {
  const cur = Number(data.current_rent || 0);
  const sug = Number(data.recommended_rent || data.suggested_rent || data.market_rent || 0);
  const delta = sug - cur;
  const pct = cur > 0 ? Math.round((delta / cur) * 100) : null;
  const Icon = delta > 50 ? TrendingUp : delta < -50 ? TrendingDown : Minus;
  const tone = delta > 50 ? "green" : delta < -50 ? "red" : "slate";

  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-green-800">Recommendation</div>
      <div className="mt-3 flex items-baseline gap-3">
        <div className="font-display text-5xl font-bold text-slate-950">${sug.toLocaleString()}</div>
        <div className="text-slate-500">/ month</div>
      </div>
      {cur > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <Pill tone={tone}><Icon className="w-3 h-3"/> {delta >= 0 ? "+" : ""}${Math.abs(delta).toLocaleString()} {pct != null ? `(${pct}%)` : ""}</Pill>
          <span className="text-sm text-slate-500">vs current ${cur.toLocaleString()}/mo</span>
        </div>
      )}
      {(data.market_low || data.range_low) != null && (
        <div className="mt-7 grid grid-cols-3 gap-4">
          <Stat label="Market low" value={`$${(data.market_low || data.range_low).toLocaleString()}`}/>
          <Stat label="Median" value={`$${(data.market_median || data.median || sug).toLocaleString()}`}/>
          <Stat label="Market high" value={`$${(data.market_high || data.range_high).toLocaleString()}`}/>
        </div>
      )}
      {(data.rationale || data.analysis || data.notes) && (
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">Analysis</div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{data.rationale || data.analysis || data.notes}</p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="font-display text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
